/*
  Shared file between components and gatsby-node files
  Should use commonJS
*/

const { languages } = require('../locales');
const schema  = require('../../site_configuration/schema.json');

const filenameSeparator = '_';

const getHomeRegionLink = codeRegion => `/PASSE/${codeRegion}`;

const getFirstArrayElement = array => (array.length > 0 ? array[0] : null);
/**
 * Create page param id
 *
 * @param {string} period
 * @param {string} domainCode
 * @param {string} family
 * @returns {string}
 */
const getPageParamsId = (period, domainCode, family) => `${period}-${domainCode}-${family}`;

const textContentConfig = {
  headline: 'ACCRO',
  title: 'TITRE',
  content: 'MESS1',
  knowMore: 'MESS2',
};

/* French is the default language in the ressources naming convention and don't appear in files name
 * If that were to change, the two following functions should be updated
 * - getLangagePrefix
 * - getStringLangagePrefix
*/

/**
 * Define language prefix as per file naming convention (fr -> null, en -> en)
 *
 * @param {string} language
 * @returns {string}
 */
const getLangagePrefix = language => (language === languages[0] ? null : language);
/**
 * Define file language suffix as per file naming convention (fr -> '', en -> _en)
 *
 * @param {string} langagePrefix
 * @returns {string}
 */
const getStringLangagePrefix = langagePrefix => (langagePrefix === null ? '' : `${filenameSeparator}${langagePrefix}`);

/**
 * Determine filename as naming convention from object input
 * @param {object} param0
 * {
 *    language, textType,
 *    domainCode, period, domainCode, family, param, timestep,
 *    productType, stationNum
 * }
 * @returns {string}
 */
const getFileName = ({
  language,
  textType = null,
  domainCode,
  period,
  family = null, param = null, timestep = null, productType = null, stationNum = null,
}) => {
  // if language is default language, it is not used in the file path
  const lang = getLangagePrefix(language);
  const keys = [
    period, domainCode, family, param, timestep, productType,
    textType, stationNum, lang,
  ];
  const activeKeys = keys.filter(item => Boolean(item));

  return activeKeys.join(filenameSeparator);
};

/**
 * From an array of ressources, find the one with the exact filename
 * and retrieve the [type] attribute content if any
 * @param {array of object} ressources // [ { name: xx, [type]: yy, ... }, ...]
 * @param {string} fileName
 * @param {string} type
 * @returns {string / object}
 */
const getContent = (ressources, fileName, type = 'htmlAst') => {
  const content = ressources
    ? ressources.find(({ name }) => name === fileName)
    : null;

  const hasContent = Boolean(content);

  return hasContent && content[type] ? content[type] : null;
};

/**
 * From paramters, define the image filename and retrieve it's relativePath
 * from the availalbe images array
 * @param {object} param0
 * { pageImages (array of object),
 * (then all string) : language, period, domainCode, family, param, timestep, productType }
 * @returns {string}
 */
const getImgPath = ({
  pageImages, language, period, domainCode, family, param, timestep, productType, stationNum = null,
}) => {
  const imgName = getFileName({
    language,
    period,
    domainCode,
    family,
    param,
    timestep,
    productType,
    stationNum,
  });
  return getContent(pageImages, imgName, 'relativePath');
};

const metropolitainArea = ['NAT', 'REG'];

const pdfFileCode = 'FPROD';
/**
 * Determine domain type when dealing with pdf files
 * for FRA domain (type: NAT, code: FRA) the used type key in pdf file naming
 * is FRA instead of NAT so we fix this
 * @param {string} type (domain type)
 * @returns {string}
 */
const getDomainTypePdf = type => (type === 'NAT' ? 'FRA' : type);

/**
 * Determine pdf filename regex from period, domainType, family, param, langPrefix
 *
 * @param {string} period
 * @param {string} domainType
 * @param {string} family
 * @param {string} param
 * @param {string} langPrefix
 * @returns {regex}
 */
const getPdfNameRegex = (period, domainType, family, param, langPrefix) => {
  const pdfDomain = getDomainTypePdf(domainType);

  /* Some synthesis page have a pdf file */
  if (!family) {
    return new RegExp(`${period}.*${pdfDomain}${filenameSeparator}${pdfFileCode}${langPrefix}$`);
  }

  /* Some pdf files are shared across domains */
  if (!domainType) {
    return new RegExp(`${period}${filenameSeparator}${family}.*${param}.*${pdfFileCode}${langPrefix}$`);
  }

  /* Some pdf files represent all params for a family */
  if (!param) {
    return new RegExp(`${period}.*${pdfDomain}.*${family}${filenameSeparator}${pdfFileCode}${langPrefix}$`);
  }

  /* General use case : pdf file name contains the domain type, the family and and the param */
  return new RegExp(`${period}.*${pdfDomain}.*${family}.*${param}.*${pdfFileCode}${langPrefix}$`);
};

const getPdfFiles = (pdfFiles, period, domainType, family, param, langPrefix) => {
  let availablePdf = null;

  const regex = getPdfNameRegex(period, domainType, family, param, langPrefix);
  availablePdf = pdfFiles.filter(({ name }) => regex.test(name));

  /*
    Only if we do not find pdf file with both family & param
    try with only family
  */
  if (availablePdf.length === 0) {
    const regexOnlyFamily = getPdfNameRegex(period, domainType, family, null, langPrefix);
    availablePdf = pdfFiles.filter(({ name }) => regexOnlyFamily.test(name));
  }
  /*
    Only if we do not find pdf file with the current domain type
    for domain in the metropolitan area (eg NAT & REG)
    try with no domainType as certain files are common to several type
  */
  if (availablePdf.length === 0 && metropolitainArea.includes(domainType)) {
    const regexNoDomain = getPdfNameRegex(period, null, family, param, langPrefix);
    availablePdf = pdfFiles.filter(({ name }) => regexNoDomain.test(name));
  }

  return availablePdf && availablePdf.map(({ name, relativePath }) => ({ name, relativePath }));
};

/**
 * Define to which domain types we can navigate to from a specific domain type
 * both NAT and REG domains can navigat to NAT and REG types, other access only their own type
 *
 * @param {string} type (domain type)
 * @returns {array of strings} (array of domain types)
 */
const getAccessibleDomainType = type => (metropolitainArea.includes(type)
  ? metropolitainArea
  : [type]);

const getDomainMapRegex = domainCode => `${domainCode}${filenameSeparator}carte$`;
const getDomainMapMiniName = domainCode => `${domainCode}${filenameSeparator}carte_mini`;
const mapMiniLangRegex = stringLangagePrefix => `.*${filenameSeparator}carte_mini${stringLangagePrefix}$`;

const animFolfer = 'anim';
const animProduct = 'ANIMA';
/**
 * Get animation image file
 * @param {*} imageFiles
 * @param {string} period
 * @param {string} domainCode
 * @param {string} langPrefix
 * @returns {string} / null
 */
const getAnimationImage = (imageFiles, period, domainCode, langPrefix) => {
  /* animation are inside an `anim` folder */
  const animImageRegex = new RegExp(`.*${period}/${domainCode}/${animFolfer}/.*`);
  /* we use the filename to select the one with the correct language */
  const animFileLangRegex = new RegExp(`.*${animProduct}${langPrefix}$`);
  const content = imageFiles
    .filter(({ relativePath, name }) => // TO BE REMOVED ?
      animImageRegex.test(relativePath) && animFileLangRegex.test(name));

  return content.length > 0 ? content[0]?.relativePath : null;
};

const dataProduct = 'DONNE';
const getDataFilename = (period, domainCode, family, param, timestep) =>
  getFileName({
    language: null, productType: dataProduct, domainCode, period, family, param, timestep,
  });

/**
 * Get paramters from : period family domainType and schema.json specification
 *
 * @param {string} period
 * @param {string} family
 * @param {string} domainType
 * @returns {array of object} // [ { "param": "TM", "timesteps": [...],  ...]
 */
const getParameters = (period, family, domainType) => {
  const existInSchema = (
    schema[period]
    && schema[period][family]
    && schema[period][family][domainType]
  );
  const parameters = existInSchema
    ? schema[period][family][domainType]
    : [];

  return parameters;
};

/**
 * Get timesteps from : period family domainType paramLabel and schema.json specification
 *
 * @param {string} period
 * @param {string} family
 * @param {string} domainType
 * @param {string} paramLabel
 * @returns {array of object} // [{ timestep: "ANN", miseEnPage: ...} ...]
 */
const getTimesteps  = (period, family, domainType, paramLabel) => {
  const paramDescription = getParameters(period, family, domainType);
  const timesteps = paramDescription?.find(({ param }) => param === paramLabel)?.timesteps;

  if (!timesteps) {
    return [];
  }
  return timesteps;
};

const matchParamFromFilenameRegex = /^[A-Z]+_[A-Z]+_[A-Z]+_([A-Z]+[0-9]*)_.*/;
/**
 * From period, family, domainType, define the available parameters
 * depending on available text files in ressources
 *
 * @param {object} param0
 * { period (string), family (string), domainType (string), textNodes (array of object)}
 * @returns {array}
 */
const getCurrentPageParameters = ({
  period,
  family,
  domainType,
  textNodes,
}) => {
  // Get possible parameters from schema configuration and their attributes
  const possibleParametersList = getParameters(period, family, domainType);

  // Get available params from files
  const availableParamsAll = textNodes
    .map(({ name }) => {
      const match = name.match(matchParamFromFilenameRegex);
      return match && match.length > 1 ? match[1] : null;
    })
    .filter(match => Boolean(match));
  const availableParams = [...new Set(availableParamsAll)];

  // Get parameters from possibles and availables ones
  return possibleParametersList && possibleParametersList
    .filter(({ param }) => availableParams.includes(param));
};

/**
 * Group params labels by parent if any
 *
 * @param {array of objects} currentFamilyParams
 * [ ... { "param": "STORM", { "param": "FREQRR", "parent": "PLUIE_HT", ... ]
 * @returns {array of objects}
 * [ ... { "label": "STORM", { group: "PLUIE_HT", "labels": ["FREQRR", ..] }, ... ]
 */
const getParametersLabels = currentFamilyParams => {
  if (!currentFamilyParams) {
    return null;
  }

  const groupedLabels = [];

  currentFamilyParams.forEach(({ param, group }) => {
    if (!group) {
      groupedLabels.push({ label: param });
    } else {
      const parentObject = groupedLabels.find(item => item?.group === group);
      if (!parentObject) {
        groupedLabels.push(({ group, labels: [param] }));
      } else {
        parentObject?.labels?.push(param);
      }
    }
  });

  return groupedLabels;
};

/**
 * Get timesteps labels from the selected param and current family parameters list
 *
 * @param {array} param [ 'ANN', 'ETE', ...]
 * @param {array of objects} currentFamilyParams [... { "param": "STORM", timesteps: [] } ...]
 *
 * @returns {array of objects} [{ label: 'ANN' }, { label: 'ETE'}, ...]
 */
const getTimestepsLabels = (param, currentFamilyParams) => {
  if (!currentFamilyParams) {
    return null;
  }

  const timesteps = currentFamilyParams.find(({ param: p }) => p === param)?.timesteps;
  if (!timesteps && timesteps.length === 0) {
    return null;
  }

  return timesteps.map(timestep => ({ label: timestep }));
};

/**
 * Creating navigation tabs
 */

/**
 * Define the path item to the chosen period domainCode family param timestep
 * depending on available parameters and timesteps defined in availablePageParams
 *
 * @param {*} availablePageParams
 * @param {string} period
 * @param {string} domainCode
 * @param {string} family
 * @param {string} param
 * @param {string} timestep
 * @returns {object} { period, domainCode, family, param, timestep }
 */
const getTargetPathItems = (
  availablePageParams, period, domainCode, family = null, param = null, timestep = null,
) => {
  const items = { period: null, domainCode: null, family: null, param: null, timestep: null };
  items.period = period;
  items.domainCode = domainCode;
  items.family = family;
  items.param = param;
  items.timestep = timestep;

  // target path depend on available routes
  const targetId = getPageParamsId(period, domainCode, family);
  const targetParams = availablePageParams.find(({ pageId }) => pageId === targetId)?.params;

  // the default param value is the first param in the available param list
  const defaultParam = targetParams && targetParams[0]?.param;
  if (!param) {
    // used when no param is provided
    items.param = defaultParam;
  } else {
    const targetParameter = targetParams && targetParams.find(({ param: p }) => p === param);
    // or when the chosen param is not available
    items.param = targetParameter ? param : defaultParam;
  }

  const targetTimesteps = targetParams && targetParams.find(({ param: p }) => p === items.param)
    ?.timesteps;
  // the default timestep value is the first timestep for the given param
  const defaultTimestep = targetTimesteps && targetTimesteps[0];
  if (!timestep) {
    // used when no timestep is provided
    items.timestep = defaultTimestep;
  } else {
    const currentTimestepAvailable = targetTimesteps && targetTimesteps.includes(timestep);
    // or when the chosen timestep is not available
    items.timestep = currentTimestepAvailable ? timestep : defaultTimestep;
  }

  return items;
};

/**
 * Define path from pathItems
 *
 * @param {object} pathItems
 * @returns {string}
 */
const getPathFromItems = pathItems => {
  const {
    period = null,
    domainCode = null,
    family = null,
    param = null,
    timestep = null,
  } = pathItems;

  // homepage
  if (!period || !domainCode) {
    return '/';
  }

  // domain synthesis page
  if (!family || !param || !timestep) {
    return `/${period}/${domainCode}`;
  }

  return `/${period}/${domainCode}/${family}/${param}/${timestep}`;
};

/**
 * Define the path to the chosen period domainCode family param timestep
 * depending on available parameters and timesteps defined in availablePageParams
 *
 * @param {array of object} availablePageParams
 * @param {string} period
 * @param {string} domainCode
 * @param {string} family
 * @param {string} param
 * @param {string} timestep
 * @returns {string}
 */
const getTargetPath = (
  availablePageParams, period, domainCode, family = null, param = null, timestep = null,
) => {
  const pathItems = getTargetPathItems(
    availablePageParams, period, domainCode, family, param, timestep,
  );
  return getPathFromItems(pathItems);
};

// to be updated with codes from source files : params, periods
const periods = [
  { period: 'PASSE', color: '#2f9dcc', gradient: 'linear-gradient(135deg, rgba(47,157,204,1) 50%, rgba(111,202,242,1) 70%)' },
  { period: 'FUTUR', color: '#bfa571', gradient: 'linear-gradient(135deg, rgba(191,165,113,1) 50%, rgba(244,213,153,1) 70%)' },
];
const defaultPeriodTabs = [
  { label: null, shortLabel: null, id: null, icon: 'home' },
  { label: 'passe', shortLabel: 'passe_short', id: periods[0].period, icon: null },
  { label: 'futur', shortLabel: 'futur_short', id: periods[1].period, icon: null },
];

/**
 * Create navigation link for periods tabs
 *
 * @param {array of object} availablePageParams
 * @param {string} domainCode
 * @param {string} family
 * @param {string} param
 * @param {string} timestep
 * @returns {array of object}
 */
const getPeriodTabs = (availablePageParams, domainCode, family, param, timestep) =>
  defaultPeriodTabs.map(tab => {
    const pathItems = getTargetPathItems(
      availablePageParams, tab.id, domainCode, family, param, timestep,
    );
    const link = getPathFromItems(pathItems);
    const differentParamTimestep = param !== pathItems?.param || timestep !== pathItems?.timestep;
    return ({
      ...tab,
      link,
      differentParamTimestep,
    });
  });

const defaultFamilyTabs = [
  { label: 'synth', shortLabel: 'synth', id: null, icon: null },
  { label: 'tempe', shortLabel: null, id: 'TEMPE', icon: 'temperature' },
  { label: 'preci', shortLabel: null, id: 'PRECI', icon: 'precipitation' },
  { label: 'event', shortLabel: null, id: 'EVENT', icon: 'event' },
  { label: 'impac', shortLabel: null, id: 'IMPAC', icon: 'impact' },
];

/**
 * Create navigation link for periods tabs
 *
 * @param {array of object} availablePageParams
 * @param {string} period
 * @param {string} domainCode
 * @param {string} param
 * @param {string} timestep
 * @returns {array of object}
 */
const getParamTabs = (availablePageParams, period, domainCode, param, timestep) =>
  defaultFamilyTabs.map(tab => ({
    ...tab,
    link: getTargetPath(availablePageParams, period, domainCode, tab.id, param, timestep),
  }));

module.exports = {
  getFirstArrayElement,
  getHomeRegionLink,
  getPageParamsId,
  getParametersLabels,
  getTimestepsLabels,
  textContentConfig,
  getFileName,
  getContent,
  getImgPath,
  getLangagePrefix,
  getStringLangagePrefix,
  getParameters,
  getPathFromItems,
  getPdfNameRegex,
  getPdfFiles,
  getDataFilename,
  getAccessibleDomainType,
  getDomainMapRegex,
  getDomainMapMiniName,
  getAnimationImage,
  getTimesteps,
  getCurrentPageParameters,
  metropolitainArea,
  getPeriodTabs,
  getParamTabs,
  getTargetPath,
  periods,
  mapMiniLangRegex,
};
